import { AssetKey } from '@constants/asset-keys.const';
import { ActionInfo } from '@models/game-config.interface';
import Phaser from 'phaser';
import { NodeVisualState } from '@enums/node-states.enum';
import { NodeLayoutItem } from '@models/layout';
import { NodeConfig, NodeView } from '@models/nodes';
import {
  DEFAULT_NODE_WIDTH,
  DEFAULT_NODE_HEIGHT,
} from '@constants/common.const';
import {
  NodeVisualStrategy,
  NodeImageKey,
  ExtrasResult,
  StateChangeHelpers,
  NodeEventContext,
} from './NodeVisualStrategy';

/**
 * Base implementation of NodeVisualStrategy with Normal node defaults.
 * Concrete strategies can extend this and override specific methods.
 */
export abstract class BaseNodeStrategy implements NodeVisualStrategy {
  getSize(): { width: number; height: number } {
    return { width: DEFAULT_NODE_WIDTH, height: DEFAULT_NODE_HEIGHT };
  }

  getDefaultTexture(_scene: Phaser.Scene): NodeImageKey {
    return AssetKey.NodeUpcoming;
  }

  getDefaultNodeImage(
    scene: Phaser.Scene,
    _action?: ActionInfo,
  ): {
    sprite: Phaser.GameObjects.Image;
    scaleX: number;
    scaleY: number;
  } {
    const baseTextureKey = this.getDefaultTexture(scene);
    const { width, height } = this.getSize();
    const tex = scene.textures
      .get(baseTextureKey)
      .getSourceImage() as HTMLImageElement;
    const scaleX = width / tex.width;
    const scaleY = height / tex.height;
    const sprite = scene.add.image(0, 0, baseTextureKey);
    sprite.setOrigin(0.5).setScale(scaleX, scaleY);
    return {
      sprite,
      scaleX,
      scaleY,
    };
  }

  getTextureForState(
    state: NodeVisualState,
    scene: Phaser.Scene,
  ): NodeImageKey | void {
    const baseTexture = this.getDefaultTexture(scene);
    if (state === NodeVisualState.Default) {
      return baseTexture;
    }
    if (state === NodeVisualState.Passed || state === NodeVisualState.Active) {
      return AssetKey.NodeCompleted;
    }
    return baseTexture;
  }

  supportsOutline(): boolean {
    return true;
  }

  createExtras(
    _scene: Phaser.Scene,
    _node: NodeLayoutItem,
    _config: NodeConfig,
  ): ExtrasResult | null {
    return null;
  }

  onStateChange(
    _view: NodeView,
    state: NodeVisualState,
    helpers: StateChangeHelpers,
  ): void {
    if (state === NodeVisualState.Default || state === NodeVisualState.Passed) {
      helpers.stopOutline();
      helpers.stopPulse();
      return;
    }

    if (state === NodeVisualState.Active) {
      helpers.startPulse();
    }
  }

  // Optional methods - no default implementation
  onPassThrough?(_context: NodeEventContext, _view: NodeView): void;

  onLanded?(_context: NodeEventContext, _view: NodeView): void;

  onRewardConfirmed?(_context: NodeEventContext, _view: NodeView): void;

  onFlashExtra?(_scene: Phaser.Scene, _view: NodeView, _message: string): Promise<void>;
}
